import React, { useEffect } from 'react';
import Footer from '../../components/Footer';
import intechsanteLogo from '../../assets/Newsletter/Intechsante.png';
import './Intechsante.css';

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const Intechsante = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Vardaan at INTECHSANTÉ Medical Expo 2026 — Panel on Interoperability in African Digital Health';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="intechsante-page">
      <header className="is-hero">
        <div className="is-container">
          <div className="is-hero-grid">
            <div>
              <div className="is-eyebrow">
                <span className="is-dot" />
                Panel Appearance
                <span className="is-pipe">·</span>
                19 June 2026
                <span className="is-pipe">·</span>
                Abidjan, Côte d&apos;Ivoire
              </div>

              <h1 className="is-hero-title">
                When health systems finally learn to <em>talk to each other.</em>
              </h1>

              <p className="is-hero-french">
                « Interopérabilité et standards techniques : clé d&apos;un écosystème de santé numérique intégré »
              </p>

              <p className="is-hero-lede">
                Vardaan Data Sciences CEO Gopakumar N. Kavunkal joins Forum INTECHSANTÉ at the Côte d&apos;Ivoire Medical Expo 2026 for Panel J2-P1 — a ninety-minute exchange on the standards, architectures, and political will required to make African digital health systems work as one.
              </p>

              <div className="is-hero-actions">
                <a href="#panel" className="is-btn is-btn-primary">
                  Read about the panel
                  <ArrowIcon />
                </a>
                <a href="#roster" className="is-btn is-btn-secondary">Meet the panellists</a>
              </div>
            </div>

            <div className="is-hero-aside">
              <img
                src={intechsanteLogo}
                alt="Forum INTECHSANTÉ — Médical Expo Côte d'Ivoire, 2e édition"
                className="is-event-logo"
              />
              <aside className="is-event-card" aria-label="Event details">
              <div className="is-event-card-label">Panel J2-P1 · Plenary Programme</div>
              <h3>Forum INTECHSANTÉ — Médical Expo Côte d&apos;Ivoire, 2<sup>e</sup> Édition</h3>
              <dl>
                <dt>Date</dt>
                <dd><span className="is-strong">Friday, 19 June 2026</span></dd>

                <dt>Time</dt>
                <dd><span className="is-strong">10:00 — 11:30</span></dd>

                <dt>Room</dt>
                <dd><span className="is-strong">Salle 2</span></dd>

                <dt>Format</dt>
                <dd>Panel · 4 speakers + 1 moderator</dd>

                <dt>Language</dt>
                <dd>French &amp; English</dd>
              </dl>
              </aside>
            </div>
          </div>
        </div>
      </header>

      <section id="panel" className="is-section is-panel-section">
        <div className="is-container">
          <div className="is-section-header">
            <div className="is-section-num">
              <span className="is-roman">i.</span>
              The Conversation
            </div>
            <h2 className="is-section-title">A continent&apos;s health data, learning to flow as one.</h2>
          </div>

          <div className="is-panel-body">
            <div className="is-panel-body-meta">
              Topic context
              <br />
              90 minutes
              <br />
              Plenary Programme
            </div>

            <div>
              <p>
                Vardaan Data Sciences is honoured to participate in the second edition of Forum INTECHSANTÉ at the Côte d&apos;Ivoire Medical Expo, with our Chief Executive Officer Gopakumar N. Kavunkal joining Panel J2-P1 on the morning of Friday, 19 June 2026. The session sits at the centre of what we believe is one of the defining infrastructure conversations for African healthcare this decade. It convenes health policy, clinical practice, public-sector technology, and enterprise systems leaders for ninety minutes of substantive exchange under the broader Médical Expo programme.
              </p>

              <p>
                African health systems today are characterised by significant fragmentation. Hospital management platforms, registries, and clinical databases sit in silos that disrupt continuity of care, complicate epidemiological surveillance, and create avoidable inefficiencies in the allocation of scarce health resources. Interoperability — the ability of these systems to exchange and meaningfully use one another&apos;s information — is the technical bedrock without which a truly integrated digital health ecosystem cannot exist.
              </p>

              <p>
                The path forward is well understood at the standards level. International frameworks such as HL7 FHIR, OpenHIE, and ICD-11 — adapted to African operational realities and supported by open technical architectures — provide a credible foundation for scale. What is harder, and what the panel intends to address directly, is the political alignment, public-private coordination, and investment in local technical capability required to move beyond isolated pilots and into continent-wide deployment.
              </p>

              <div className="is-tag-row" aria-label="Standards and frameworks">
                <span className="is-tag">HL7 FHIR</span>
                <span className="is-tag">OpenHIE</span>
                <span className="is-tag">ICD-11</span>
                <span className="is-tag">Open Architectures</span>
                <span className="is-tag">Regional Standards</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="is-section is-ceo-section">
        <div className="is-container">
          <div className="is-section-header">
            <div className="is-section-num">
              <span className="is-roman">ii.</span>
              At the Table
            </div>
            <h2 className="is-section-title">Twenty-seven years of enterprise discipline, brought to the table.</h2>
          </div>

          <div className="is-ceo-grid">
            <aside className="is-ceo-stat-block" aria-label="CEO experience">
              <div className="is-stat-card">
                <div className="is-number">27<span style={{ fontSize: '0.6em' }}>+</span></div>
                <div className="is-label">Years across enterprise technology, applications, product &amp; operations</div>
              </div>
              <ul className="is-stat-mini-list">
                <li><span className="is-label-l">Role</span> <span>CEO, VDS</span></li>
                <li><span className="is-label-l">Since</span> <span>Mar 2025</span></li>
                <li><span className="is-label-l">Prior</span> <span>Saksoft</span></li>
                <li><span className="is-label-l">Prior</span> <span>Quadrant 4</span></li>
                <li><span className="is-label-l">Prior</span> <span>Mascon</span></li>
              </ul>
            </aside>

            <div className="is-ceo-body">
              <p>
                Gopakumar N. Kavunkal brings over twenty-seven years of leadership experience across enterprise technology, applications engineering, product development, sales, and operations. Prior to Vardaan Data Sciences, he led strategic business units at Mascon, Quadrant 4, and Saksoft, delivering complex enterprise solutions across financial services, healthcare, and adjacent regulated industries. He now leads Vardaan Data Sciences, overseeing governance, customer acquisition, product and service modelling, and the operating discipline that allows a young AI-first company to take on the most demanding categories of enterprise software.
              </p>

              <p>
                What he brings to a panel on health data interoperability is the perspective of someone who has spent decades building inside environments where governance, data trust, and regulatory complexity define what is operationally possible. The questions African health systems are grappling with — how to connect siloed information, how to embed accountability into shared infrastructure, how to convert fragmented data into decisions clinicians and policymakers can act on — are the same questions modern enterprises everywhere are facing, only with higher stakes because the unit of outcome is a patient rather than a transaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="is-section is-vardaan-section">
        <div className="is-container">
          <div className="is-section-header">
            <div className="is-section-num">
              <span className="is-roman">iii.</span>
              Why Vardaan
            </div>
            <h2 className="is-section-title">The plumbing of trust, applied where it matters most.</h2>
          </div>

          <div className="is-vardaan-body">
            <div className="is-vardaan-body-meta">
              Company
              <br />
              Founded 2023
              <br />
              Hyderabad &amp; Dubai
            </div>

            <div>
              <p>
                Vardaan Data Sciences operates at the intersection of artificial intelligence, governance, risk and compliance, third-party risk management, cybersecurity, identity, and operational intelligence. Our flagship platform <em>RiskaVaire GRC/TPRM</em>, alongside our AI-enabled transformation services, serves organisations across banking, financial services, insurance, healthcare, manufacturing, pharmaceuticals, government, and critical infrastructure. The discipline we bring to enterprise GRC — turning fragmented data, manual oversight, and inconsistent controls into continuous, intelligence-led operations — translates directly into the architectural questions African digital health is now asking.
              </p>

              <p>
                We do not look at AI as a standalone product or a fashionable layer applied to existing systems. We see it as practical infrastructure — the connective tissue between data, decisions, and accountability that every modern, regulated organisation requires. Healthcare is among the most demanding applications of that idea, because the cost of fragmentation is measured not only in efficiency but in patient outcomes, epidemiological readiness, and equitable access to care.
              </p>

              <div className="is-product-pills" aria-label="Vardaan platforms">
                <div className="is-product-pill"><span className="is-lead">Flagship</span>RiskaVaire GRC / TPRM</div>
                <div className="is-product-pill"><span className="is-lead">Domain</span>Cybersecurity &amp; IAM</div>
                <div className="is-product-pill"><span className="is-lead">Domain</span>Operational Intelligence</div>
                <div className="is-product-pill"><span className="is-lead">Domain</span>AI-Enabled Transformation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="is-section is-vision-section">
        <div className="is-container">
          <div className="is-section-header">
            <div className="is-section-num">
              <span className="is-roman">iv.</span>
              The Longer Arc
            </div>
            <h2 className="is-section-title">An early chapter in a longer commitment.</h2>
          </div>

          <div className="is-vision-grid">
            <div className="is-vision-meta">
              Outlook
              <br />
              2026 onward
            </div>

            <div className="is-vision-body">
              <p>
                Our conviction is that the next defining layer of enterprise software — across industries, but with particular force in healthcare — is the trust and intelligence layer that lets organisations stay accountable, resilient, and decisive at the speed AI demands. The fragmentation that African health systems are working to overcome today is a more acute version of a problem every modern enterprise is now facing. The technical answers will look different in different contexts, but the underlying architecture is the same: connected systems, shared standards, governed data, and intelligence that augments rather than replaces clinical and operational judgement.
              </p>

              <blockquote className="is-pull-quote">
                &ldquo;The cost of fragmentation in healthcare is not measured only in efficiency. It is measured in continuity of care, in surveillance readiness, and in equitable access.&rdquo;
              </blockquote>

              <p>
                We approach this panel less as a stage and more as a learning convening. The most interesting work in interoperability is being done by practitioners closer to the ground than any software company. We come to listen to the public sector, the clinical community, and the architects of the projects already underway across the continent — and to contribute what we have learned from building governance and risk infrastructure for the most regulated environments in the world. Vardaan will engage with this market for the long term, and this is an early chapter of that commitment.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="roster" className="is-section is-roster-section">
        <div className="is-container">
          <div className="is-section-header is-roster-header">
            <div className="is-section-num">
              <span className="is-roman">v.</span>
              The Panel
            </div>
            <h2 className="is-section-title">Four perspectives, one question.</h2>
          </div>

          <div className="is-roster" role="list">
            <article className="is-roster-card" role="listitem">
              <div className="is-roster-role">Moderator</div>
              <div className="is-roster-name">Jean DAHO</div>
              <div className="is-roster-affiliation">Co-Founder, AkwaTalents</div>
              <span className="is-roster-country">Côte d&apos;Ivoire</span>
            </article>

            <article className="is-roster-card" role="listitem">
              <div className="is-roster-role">Panellist</div>
              <div className="is-roster-name">Dr Jean-Maurin SERY</div>
              <div className="is-roster-affiliation">President, PSPSCI</div>
              <span className="is-roster-country">Côte d&apos;Ivoire</span>
            </article>

            <a
              className="is-roster-card is-featured is-roster-card-link"
              href="https://www.linkedin.com/in/gopakumarkavunkal/"
              target="_blank"
              rel="noopener noreferrer"
              role="listitem"
              aria-label="Gopakumar N. Kavunkal — view LinkedIn profile"
            >
              <div className="is-roster-role">Panellist</div>
              <div className="is-roster-name">Gopakumar N. Kavunkal</div>
              <div className="is-roster-affiliation">CEO, Vardaan Data Sciences</div>
              <span className="is-roster-country">India</span>
            </a>

            <article className="is-roster-card" role="listitem">
              <div className="is-roster-role">Panellist</div>
              <div className="is-roster-name">Benzo BAGHERI</div>
              <div className="is-roster-affiliation">CTO, Tech and Beyond IT</div>
              <span className="is-roster-country">Angola</span>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Intechsante;
